import { useState, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { ServiceCategory as Category } from '@/shared/model';

interface CategoriesTableProps {
    categories: Category[];
    onUpdate: (id: string, field: keyof Category, value: string | number | boolean) => void;
    onDelete: (id: string) => void;
}

export const CategoriesTable: FC<CategoriesTableProps> = ({ 
    categories, 
    onUpdate, 
    onDelete 
}) => {
    const { t } = useTranslation();
    const [exitingIds, setExitingIds] = useState<string[]>([]);

    const handleRemoveWithAnimation = (id: string) => {
        setExitingIds(prev => [...prev, id]);
        setTimeout(() => {
            onDelete(id);
            setExitingIds(prev => prev.filter(eid => eid !== id));
        }, 300);
    };

    return (
        <table className="w-full text-sm text-left">
            <thead className="text-[10px] text-slate-500 dark:text-slate-400 uppercase bg-blue-50/60 dark:bg-slate-800/80 backdrop-blur-sm border-b border-blue-100 dark:border-slate-800 sticky top-0 z-20 shadow-sm">
                <tr>
                    <th className="px-6 py-4 font-semibold tracking-wider">{t('catalog.table.category_name', 'Название категории')}</th>
                    <th className="hidden md:table-cell px-4 py-4 font-semibold tracking-wider">{t('catalog.table.surface_type', 'Тип поверхности')}</th>
                    <th className="hidden md:table-cell w-16"></th>
                </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                {categories.map(c => {
                    const isExiting = exitingIds.includes(c.id);
                    const type = c.type || 'general';
                    
                    return (
                        <tr 
                            key={c.id} 
                            className={`
                                transition-colors duration-200 ease-in-out
                                ${isExiting ? 'bg-red-50 dark:bg-red-900/10 pointer-events-none' : 'hover:bg-blue-50/30 dark:hover:bg-blue-900/10'}
                            `}
                        >
                            <td className={`px-6 transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${isExiting ? '!py-0 !border-0' : 'py-4'}`}>
                                <div className={`transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] overflow-hidden ${isExiting ? 'max-h-0 opacity-0' : 'max-h-20 opacity-100'}`}>
                                    <div className="flex items-center justify-between gap-2">
                                        <input 
                                            value={c.name}
                                            onChange={(e) => onUpdate(c.id, 'name', e.target.value)}
                                            className="bg-transparent border-b border-transparent hover:border-slate-300 dark:hover:border-slate-600 focus:border-blue-400 dark:focus:border-blue-500 focus:outline-none transition-colors duration-150 font-bold text-slate-700 dark:text-slate-200 py-1 w-full"
                                        />
                                        <div className="md:hidden flex items-center gap-2">
                                            <div className="bg-slate-50 dark:bg-slate-800 rounded-md px-2 py-1 h-8 flex items-center border border-slate-100 dark:border-slate-700">
                                                <select 
                                                    value={type}
                                                    onChange={(e) => onUpdate(c.id, 'type', e.target.value)}
                                                    className="bg-transparent text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase outline-none appearance-none"
                                                >
                                                    <option value="general" className="dark:bg-slate-800">{t('catalog.types.general', 'Общее')}</option>
                                                    <option value="wall" className="dark:bg-slate-800">{t('catalog.types.wall', 'Стены')}</option>
                                                    <option value="floor" className="dark:bg-slate-800">{t('catalog.types.floor', 'Пол')}</option>
                                                    <option value="ceiling" className="dark:bg-slate-800">{t('catalog.types.ceiling', 'Потолок')}</option>
                                                </select>
                                            </div>
                                            <button
                                                onClick={() => handleRemoveWithAnimation(c.id)}
                                                aria-label={t('catalog.delete_category', 'Удалить категорию')}
                                                className="w-8 h-8 flex items-center justify-center text-slate-300 dark:text-slate-600 hover:text-red-500 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                            >
                                                <Trash2 size={18} aria-hidden="true" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td className={`hidden md:table-cell px-4 transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${isExiting ? '!py-0 !border-0' : 'py-4'}`}>
                                <div className={`transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] overflow-hidden ${isExiting ? 'max-h-0 opacity-0' : 'max-h-20 opacity-100'}`}>
                                    <select 
                                        value={type}
                                        onChange={(e) => onUpdate(c.id, 'type', e.target.value)}
                                        className="bg-transparent text-xs font-bold text-slate-500 dark:text-slate-400 uppercase w-full cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 focus:text-blue-600 dark:focus:text-blue-400 border-b border-transparent hover:border-slate-300 dark:hover:border-slate-600 focus:border-blue-400 dark:focus:border-blue-500 focus:outline-none transition-colors duration-150 py-1 appearance-none"
                                    >
                                        <option value="general" className="dark:bg-slate-900">{t('catalog.types.general', 'Общее')}</option>
                                        <option value="wall" className="dark:bg-slate-900">{t('catalog.types.wall', 'Стены')}</option>
                                        <option value="floor" className="dark:bg-slate-900">{t('catalog.types.floor', 'Пол')}</option>
                                        <option value="ceiling" className="dark:bg-slate-900">{t('catalog.types.ceiling', 'Потолок')}</option>
                                    </select>
                                </div>
                            </td>
                            <td className={`hidden md:table-cell px-4 text-right transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${isExiting ? '!py-0 !border-0' : 'py-4'}`}>
                                <div className={`transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] overflow-hidden ${isExiting ? 'max-h-0 opacity-0' : 'max-h-20 opacity-100'}`}>
                                    <button
                                        onClick={() => handleRemoveWithAnimation(c.id)}
                                        aria-label={t('catalog.delete_category', 'Удалить категорию')}
                                        className="p-2 text-slate-300 dark:text-slate-600 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                    >
                                        <Trash2 size={16} aria-hidden="true" />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
};
